import { useEffect, useRef, useState } from "react";

type Props = {
  query: string;
  title: string;
  ariaLabel: string;
  height?: string; // tailwind classes for height, e.g. "h-[320px] md:h-[380px]"
  zoom?: number;
};

/**
 * Accessible, performance-friendly Google Maps embed.
 * - Renders a lightweight static fallback (no third-party JS/network) on load.
 * - Only mounts the iframe once the container scrolls into view.
 * - Users on reduced-data / no-JS still get a keyboard-focusable link
 *   to the map on google.com.
 */
export function LazyMap({
  query,
  title,
  ariaLabel,
  height = "h-[320px] md:h-[380px]",
  zoom = 12,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!containerRef.current || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, [inView]);

  const embedSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    query,
  )}&z=${zoom}&output=embed`;
  const externalHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query,
  )}`;
  const shouldMount = inView || activated;

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${height} rounded-2xl overflow-hidden border border-border bg-muted`}
      role="region"
      aria-label={ariaLabel}
    >
      {/* Static fallback — always in the DOM for a11y + no-JS users */}
      <div
        aria-hidden={loaded}
        className={`absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center transition-opacity duration-500 ${
          loaded ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--background)) 100%)",
        }}
      >
        <svg
          aria-hidden="true"
          focusable="false"
          width="36"
          height="36"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          className="text-primary"
        >
          <path d="M12 22s-7-6.2-7-12a7 7 0 1 1 14 0c0 5.8-7 12-7 12z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
        <p className="text-sm text-foreground/90 font-medium">{title}</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          Interactive map loads on scroll to keep this page fast.
        </p>
        {!inView && (
          <button
            type="button"
            onClick={() => setActivated(true)}
            className="mt-1 inline-flex items-center gap-2 rounded-full border border-border-strong px-4 py-2 text-xs font-semibold hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Load map now
          </button>
        )}
        <a
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          Open in Google Maps ↗
        </a>
      </div>

      {shouldMount && (
        <iframe
          title={title}
          aria-label={ariaLabel}
          src={embedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full border-0 transition-opacity duration-500 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}
